export const apis = [
  {
    id: "login",
    name: "Corporate Authorize",
    method: "POST",
    endpoint: "/corporate/authorize",
    description:
      "Authenticates a corporate vendor using a pre-issued bearer key and returns a JWT token. Optionally registers an HTTPS webhook URL to receive real-time booking status updates.",
    headers: [
      {
        key: "bearerkey",
        value: "XXXXXXXXXXXXXXXXXXXX",
        required: true,
        description: "Pre-issued vendor key. Must contain @786 followed by your company ID.",
      },
      {
        key: "Content-Type",
        value: "application/json",
        required: true,
        description: "Request body format.",
      },
    ],
    body: [
      {
        field: "web_hook_url",
        type: "string",
        required: false,
        description: "Your HTTPS callback URL to receive booking status updates. Must start with https://.",
        example: "https://yourvendor.com/booking/callback",
      },
    ],
    requestExample: `{
  "web_hook_url": "https://yourvendor.com/booking/callback"
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Success",
        example: `{
  "status": true,
  "statusCode": 200,
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy"
}`,
      },
      {
        code: 401,
        type: "error",
        label: "Invalid Bearer Key",
        example: `{
  "status": false,
  "statusCode": 401,
  "message": "Invalid Bearer key provided"
}`,
      },
      {
        code: 403,
        type: "error",
        label: "Bearer Key Inactive",
        example: `{
  "status": false,
  "statusCode": 403,
  "message": "bearerkey is inactive"
}`,
      },
      {
        code: 1000,
        type: "error",
        label: "Webhook Not HTTPS",
        example: `{
  "status": false,
  "statusCode": 1000,
  "message": "Webhook URL has not secure protocol, it should be https"
}`,
      },
      {
        code: 1001,
        type: "error",
        label: "Invalid Webhook URL",
        example: `{
  "status": false,
  "statusCode": 1001,
  "message": "Webhook URL are invalid"
}`,
      },
      {
        code: 1002,
        type: "error",
        label: "Corporate Inactive",
        example: `{
  "status": false,
  "statusCode": 1002,
  "message": "Corporate is inactive."
}`,
      },
      {
        code: 500,
        type: "error",
        label: "Server Error",
        example: `{
  "status": false,
  "statusCode": 500,
  "message": "Internal Server Error"
}`,
      },
    ],
    notes: [
      "Save the token — it is required in all subsequent API calls.",
      "The webhook URL is stored permanently. You only need to set it once.",
      "To update your webhook URL, login again with the new URL.",
      "Keep your bearerkey confidential — it is your permanent credential.",
    ],
  },

  {
    id: "estimated-fare",
    name: "Get Estimated Price",
    method: "POST",
    endpoint: "/corporate/get/estimated/fare",
    description:
      "Returns estimated fare breakdown for all available car types between a pickup and drop location. Supports both immediate and scheduled bookings.",
    headers: [
      {
        key: "bearerkey",
        value: "xxxxx@786{companyId}",
        required: true,
        description: "Same bearerkey used in Corporate Authorize.",
      },
      {
        key: "Content-Type",
        value: "application/json",
        required: true,
        description: "Request body format.",
      },
    ],
    body: [
      { field: "bookingType", type: "string", required: true, description: 'Type of booking. Allowed: "cityRide" or "delivery".', example: "cityRide" },
      { field: "pickupLocation", type: "object", required: true, description: "Pickup coordinates and address.", example: '{ "lat": 29.37, "long": 47.97, "address": "Kuwait City" }' },
      { field: "dorpLocation", type: "object", required: true, description: "Drop coordinates and address.", example: '{ "lat": 29.33, "long": 48.01, "address": "Salmiya" }' },
      { field: "userId", type: "string", required: false, description: "Corporate user ID. Required if booking is for a specific user.", example: "USR001" },
      { field: "pickup_date_time", type: "string", required: false, description: 'Scheduled pickup time. Format: "YYYY-MM-DD HH:mm:ss". Omit for immediate booking.', example: "2026-07-14 15:00:00" },
    ],
    requestExample: `{
  "bookingType": "cityRide",
  "pickupLocation": {
    "lat": 29.3759,
    "long": 47.9774,
    "address": "Kuwait City, Kuwait"
  },
  "dorpLocation": {
    "lat": 29.3326,
    "long": 48.0719,
    "address": "Salmiya, Kuwait"
  },
  "pickup_date_time": "2026-07-14 15:00:00"
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Success",
        example: `{
  "status": true,
  "statusCode": 200,
  "data": {
    "currency": "KWD",
    "distance": 8.5,
    "duration": 18,
    "carTypeRentData": [
      {
        "carTypeId": "abc123",
        "name": "Economy",
        "carSeatCapacity": 4,
        "amount": 3.500,
        "serviceCharges": 0.200,
        "airportParking": 0.000,
        "toll": 0.000,
        "discountAmount": 0.000,
        "SchedulingFee": 0.000,
        "finalAmount": 3.700
      }
    ]
  }
}`,
      },
      { code: 422, type: "error", label: "Missing Parameters", example: `{\n  "status": false,\n  "statusCode": 422,\n  "message": "Required Parameters Missing"\n}` },
      { code: 1004, type: "error", label: "Out of City", example: `{\n  "status": false,\n  "statusCode": 1004,\n  "message": "This address is out of city"\n}` },
      { code: 1005, type: "error", label: "Delivery Unavailable", example: `{\n  "status": false,\n  "statusCode": 1005,\n  "message": "delivery not available this area"\n}` },
      { code: 1006, type: "error", label: "Restricted Area", example: `{\n  "status": false,\n  "statusCode": 1006,\n  "message": "this address comes in restricted area"\n}` },
      { code: 1007, type: "error", label: "No Cars Available", example: `{\n  "status": false,\n  "statusCode": 1007,\n  "message": "no available cars"\n}` },
      { code: 1012, type: "error", label: "User Not In Corporate", example: `{\n  "status": false,\n  "statusCode": 1012,\n  "message": "User's Not Existing In corporate."\n}` },
      { code: 500, type: "error", label: "Server Error", example: `{\n  "status": false,\n  "statusCode": 500,\n  "message": "Internal Server Error"\n}` },
    ],
    notes: [
      "Use the carTypeId from the response when calling Create Order.",
      "finalAmount includes service charges, toll, airport parking, discount, and scheduling fee.",
      "If pickup_date_time is in the past or less than the configured threshold, the booking is treated as immediate.",
    ],
  },

  {
    id: "nearest-driver",
    name: "Get Nearest Driver",
    method: "POST",
    endpoint: "/corporate/get/nearest/driver",
    description:
      "Returns the estimated arrival time (ETA) in minutes for the nearest available driver for each requested car type.",
    headers: [
      { key: "bearerkey", value: "xxxxx@786{companyId}", required: true, description: "Same bearerkey used in Corporate Authorize." },
      { key: "Content-Type", value: "application/json", required: true, description: "Request body format." },
    ],
    body: [
      { field: "carTypeIds", type: "array", required: true, description: "Array of car type IDs to check availability for.", example: '["abc123", "def456"]' },
      { field: "pickuplat", type: "number", required: true, description: "Pickup latitude coordinate.", example: "29.3759" },
      { field: "pickuplong", type: "number", required: true, description: "Pickup longitude coordinate.", example: "47.9774" },
    ],
    requestExample: `{
  "carTypeIds": ["abc123", "def456"],
  "pickuplat": 29.3759,
  "pickuplong": 47.9774
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Success",
        example: `{
  "status": true,
  "statusCode": 200,
  "data": [
    { "carTypeId": "abc123", "duration": 5 },
    { "carTypeId": "def456", "duration": null }
  ]
}`,
      },
      { code: 422, type: "error", label: "Missing Parameters", example: `{\n  "status": false,\n  "statusCode": 422,\n  "message": "Required Parameters Missing"\n}` },
      { code: 500, type: "error", label: "Server Error", example: `{\n  "status": false,\n  "statusCode": 500,\n  "message": "Internal Server Error"\n}` },
    ],
    notes: [
      "duration is in minutes.",
      "duration: null means no driver is currently available for that car type.",
      "Use this before Create Order to show estimated pickup time to the passenger.",
    ],
  },

  {
    id: "create-order",
    name: "Create Order",
    method: "POST",
    endpoint: "/corporate/create/booking",
    description:
      "Creates a new ride booking on behalf of a corporate vendor for a passenger. Supports both immediate and scheduled bookings. A webhook callback is fired after creation.",
    headers: [
      { key: "bearerkey", value: "xxxxx@786{companyId}", required: true, description: "Same bearerkey used in Corporate Authorize." },
      { key: "Content-Type", value: "application/json", required: true, description: "Request body format." },
    ],
    body: [
      { field: "user_name", type: "string", required: true, description: "Passenger full name.", example: "John Doe" },
      { field: "user_email", type: "string", required: false, description: "Passenger email address.", example: "john@example.com" },
      { field: "country_code", type: "string", required: true, description: 'Country code with + prefix. e.g. "+965"', example: "+965" },
      { field: "user_phone", type: "string", required: true, description: "Passenger mobile number without country code.", example: "55001234" },
      { field: "pickupLocation", type: "object", required: true, description: "Pickup coordinates and address.", example: '{ "lat": 29.37, "long": 47.97, "address": "Kuwait City" }' },
      { field: "dorpLocation", type: "object", required: true, description: "Drop coordinates and address.", example: '{ "lat": 29.33, "long": 48.01, "address": "Salmiya" }' },
      { field: "carTypeId", type: "string", required: true, description: "Car type ID from Get Estimated Price response.", example: "abc123" },
      { field: "bookingType", type: "string", required: true, description: '"cityRide" or "delivery".', example: "cityRide" },
      { field: "pickup_date_time", type: "string", required: false, description: 'Scheduled pickup. Format: "YYYY-MM-DD HH:mm:ss". Omit for immediate booking.', example: "2026-07-14 15:00:00" },
      { field: "userId", type: "string", required: false, description: "Corporate user ID.", example: "USR001" },
    ],
    requestExample: `{
  "user_name": "John Doe",
  "user_email": "john@example.com",
  "country_code": "+965",
  "user_phone": "55001234",
  "pickupLocation": {
    "lat": 29.3759,
    "long": 47.9774,
    "address": "Kuwait City, Kuwait"
  },
  "dorpLocation": {
    "lat": 29.3326,
    "long": 48.0719,
    "address": "Salmiya, Kuwait"
  },
  "carTypeId": "abc123",
  "bookingType": "cityRide",
  "pickup_date_time": "2026-07-14 15:00:00"
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Booking Created",
        example: `{
  "status": true,
  "statusCode": 200,
  "data": {
    "booking_id": "BK123456",
    "display_book_id": "WK-001",
    "status": "PENDING",
    "amount": "KWD 5.500",
    "customer_info": [
      { "name": "John Doe", "country_code": "+965", "phone": "55001234", "email": "john@example.com" }
    ],
    "pick_info": { "lat": 29.3759, "long": 47.9774, "address": "Kuwait City" },
    "drop_info": { "lat": 29.3326, "long": 48.0719, "address": "Salmiya" },
    "driver_info": [{ "name": "", "phone": "", "location": { "latitude": 0, "longitude": 0 } }],
    "vehicle_info": [{ "car": "", "model": "", "brand": "", "color": "", "license_plate": "" }],
    "completed_timeline": [{
      "create_date_time": "2026-07-13 14:00:00",
      "assign_date_time": "2026-07-14 14:45:00",
      "pickup_date_time": "2026-07-14 15:00:00",
      "accept_date_time": "",
      "arrive_date_time": "",
      "begin_date_time": "",
      "drop_date_time": "",
      "complete_date_time": "",
      "cancel_date_time": ""
    }]
  }
}`,
      },
      { code: 422, type: "error", label: "Missing Parameters", example: `{\n  "status": false,\n  "statusCode": 422,\n  "message": "Required Parameters Missing"\n}` },
      { code: 1012, type: "error", label: "User Not In Corporate", example: `{\n  "status": false,\n  "statusCode": 1012,\n  "message": "User's Not Existing In corporate."\n}` },
      { code: 1017, type: "error", label: "Car Pricing Not Set", example: `{\n  "status": false,\n  "statusCode": 1017,\n  "message": "Create Order Failed ,Selected Car Pricing Is Not Set"\n}` },
      { code: 1018, type: "error", label: "Auth Mismatch", example: `{\n  "status": false,\n  "statusCode": 1018,\n  "message": "Authorization failed. The bearer key or user ID is invalid or mismatched."\n}` },
      { code: 500, type: "error", label: "Server Error", example: `{\n  "status": false,\n  "statusCode": 500,\n  "message": "Internal Server Error"\n}` },
    ],
    notes: [
      "Payment type is set to corporate wallet automatically.",
      "A webhook callback is fired to your registered callback_url immediately after booking is created.",
      "driver_info and vehicle_info are empty at creation — they are updated via webhook as the booking progresses.",
      "booking_id is used in Edit Order and Cancel Order APIs.",
    ],
  },

  {
    id: "booking-list",
    name: "Get Booking List",
    method: "POST",
    endpoint: "/corporate/get/booking/list",
    description:
      "Fetches a single booking by ID, or a paginated list filtered by status. Live statuses use MongoDB; COMPLETED and CANCELLED use MySQL history.",
    headers: [
      { key: "bearerkey", value: "xxxxx@786{companyId}", required: true, description: "Same bearerkey used in Corporate Authorize." },
      { key: "Content-Type", value: "application/json", required: true, description: "Request body format." },
    ],
    body: [
      { field: "token", type: "string", required: true, description: "JWT token received from Corporate Authorize API.", example: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy" },
      { field: "status", type: "string", required: false, description: "Required for list mode. Case-insensitive. Live: PENDING, SEARCHING, ASSIGN, ACCEPT, UNASSIGN, ARRIVED AT CUSTOMER, ONROUTE, ARRIVED AT DROP. History: COMPLETED, CANCELLED. Not needed when booking_id or display_book_id is sent.", example: "ACCEPT" },
      { field: "booking_id", type: "string", required: false, description: "Get one booking by internal ID. Status not required. Checks MongoDB first, then MySQL.", example: "abc123-booking-id" },
      { field: "display_book_id", type: "string", required: false, description: "Get one booking by display ID. Status not required.", example: "WB-12345" },
      { field: "limit", type: "integer", required: false, description: "Page size. Min 1, Max 20. Default 20.", example: "10" },
      { field: "offset", type: "integer", required: false, description: "Records to skip for pagination. Min 0. Default 0.", example: "0" },
      { field: "userId", type: "string", required: false, description: "Filter by corporate user ID.", example: "USR001" },
      { field: "from_date", type: "string", required: false, description: "Start date filter. Format: YYYY-MM-DD.", example: "2026-07-01" },
      { field: "to_date", type: "string", required: false, description: "End date filter. Format: YYYY-MM-DD.", example: "2026-07-16" },
      { field: "search", type: "string", required: false, description: "Search by booking ID, display ID, or customer phone/name.", example: "96512345678" },
    ],
    requestExample: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.xxxx.yyyy",
  "status": "ACCEPT",
  "limit": 10,
  "offset": 0
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Success",
        example: `{
  "status": true,
  "statusCode": 200,
  "data": {
    "total": 50,
    "limit": 10,
    "offset": 0,
    "source": "mongo",
    "bookings": [
      {
        "booking_id": "abc123",
        "display_book_id": "WB-12345",
        "status": "ACCEPT",
        "amount": "KWD 5.500",
        "customer_info": [
          { "name": "John Doe", "country_code": "+965", "phone": "12345678", "email": "john@example.com" }
        ],
        "pick_info": { "lat": 29.3759, "long": 47.9774, "address": "Pickup Address" },
        "drop_info": { "lat": 29.3117, "long": 47.4818, "address": "Drop Address" },
        "driver_info": [
          { "name": "Driver Name", "country_code": "+965", "phone": "98765432", "email": "", "location": { "latitude": 0, "longitude": 0 } }
        ],
        "vehicle_info": [
          { "car": "Sedan", "model": "Camry", "brand": "Toyota", "color": "White", "license_plate": "ABC 123" }
        ],
        "completed_timeline": [
          {
            "create_date_time": "2026-07-16 10:00:00",
            "assign_date_time": "",
            "pickup_date_time": "",
            "accept_date_time": "2026-07-16 10:05:00",
            "arrive_date_time": "",
            "begin_date_time": "",
            "drop_date_time": "",
            "complete_date_time": "",
            "cancel_date_time": ""
          }
        ]
      }
    ]
  }
}`,
      },
      { code: 401, type: "error", label: "Invalid Bearer Key", example: `{\n  "status": false,\n  "statusCode": 401,\n  "message": "Provided Bearer Key Is Not Valid"\n}` },
      { code: 403, type: "error", label: "Token Wrong Or Expired", example: `{\n  "status": false,\n  "statusCode": 403,\n  "message": "Token Is Wrong Or Expired"\n}` },
      { code: 422, type: "error", label: "Missing Parameters", example: `{\n  "status": false,\n  "statusCode": 422,\n  "message": "Required Parameters Missing"\n}` },
      { code: 1014, type: "error", label: "Booking Not Found", example: `{\n  "status": false,\n  "statusCode": 1014,\n  "message": "Booking Not Found"\n}` },
      { code: 1024, type: "error", label: "Invalid Limit", example: `{\n  "status": false,\n  "statusCode": 1024,\n  "message": "Limit must be between 1 and 20"\n}` },
      { code: 1025, type: "error", label: "Invalid Status", example: `{\n  "status": false,\n  "statusCode": 1025,\n  "message": "Invalid status filter"\n}` },
      { code: 500, type: "error", label: "Server Error", example: `{\n  "status": false,\n  "statusCode": 500,\n  "message": "Internal Server Error"\n}` },
    ],
    notes: [
      "Mode A (single booking): send booking_id OR display_book_id — status is not required. Checks MongoDB first, then MySQL.",
      "Mode B (list): send status (required). Optional filters: limit, offset, userId, from_date, to_date, search.",
      "Live statuses (PENDING, SEARCHING, ASSIGN, ACCEPT, UNASSIGN, ARRIVED AT CUSTOMER, ONROUTE, ARRIVED AT DROP) come from MongoDB.",
      "History statuses (COMPLETED, CANCELLED) come from MySQL.",
      "source is \"mongo\" for live lists and \"sql\" for history. It is omitted for single booking by ID.",
      "limit max is 20. Status is case-insensitive. Response booking shape matches Create Order.",
    ],
  },

  {
    id: "edit-order",
    name: "Edit Order",
    method: "POST",
    endpoint: "/corporate/edit/booking",
    description:
      "Edits an existing booking. Allows updating passenger info, pickup/drop location, and scheduled time. Subject to restrictions based on current booking status.",
    headers: [
      { key: "bearerkey", value: "xxxxx@786{companyId}", required: true, description: "Same bearerkey used in Corporate Authorize." },
      { key: "Content-Type", value: "application/json", required: true, description: "Request body format." },
    ],
    body: [
      { field: "booking_id", type: "string", required: true, description: "The booking ID to edit. Received from Create Order.", example: "BK123456" },
      { field: "user_name", type: "string", required: false, description: "Updated passenger name.", example: "Jane Doe" },
      { field: "user_email", type: "string", required: false, description: "Updated passenger email.", example: "jane@example.com" },
      { field: "country_code", type: "string", required: false, description: "Updated country code.", example: "+965" },
      { field: "user_phone", type: "string", required: false, description: "Updated phone number.", example: "55009999" },
      { field: "pickupLocation", type: "object", required: false, description: "Updated pickup location. Cannot edit after booking is ACCEPTED.", example: '{ "lat": 29.37, "long": 47.97, "address": "..." }' },
      { field: "dorpLocation", type: "object", required: false, description: "Updated drop location.", example: '{ "lat": 29.33, "long": 48.01, "address": "..." }' },
      { field: "pickup_date_time", type: "string", required: false, description: "Updated scheduled time. Cannot edit after booking is ACCEPTED.", example: "2026-07-14 16:00:00" },
    ],
    requestExample: `{
  "booking_id": "BK123456",
  "user_name": "Jane Doe",
  "user_phone": "55009999",
  "dorpLocation": {
    "lat": 29.2800,
    "long": 48.0800,
    "address": "Mahboula, Kuwait"
  }
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Booking Updated",
        example: `{
  "status": true,
  "statusCode": 200,
  "data": {
    "booking_id": "BK123456",
    "display_book_id": "WK-001",
    "status": "PENDING",
    "amount": "KWD 6.200",
    "customer_info": [{ "name": "Jane Doe", "country_code": "+965", "phone": "55009999", "email": "" }],
    "pick_info": { "lat": 29.3759, "long": 47.9774, "address": "Kuwait City" },
    "drop_info": { "lat": 29.2800, "long": 48.0800, "address": "Mahboula" }
  }
}`,
      },
      { code: 422, type: "error", label: "Missing Parameters", example: `{\n  "status": false,\n  "statusCode": 422,\n  "message": "Required Parameters Missing"\n}` },
      { code: 1020, type: "error", label: "Ride Ongoing", example: `{\n  "status": false,\n  "statusCode": 1020,\n  "message": "User Cant Edit Booking is OnGoing"\n}` },
      { code: 1021, type: "error", label: "Field Locked", example: `{\n  "status": false,\n  "statusCode": 1021,\n  "message": "PICKUP LOCATION cannot be edited once the booking is accepted."\n}` },
      { code: 1022, type: "error", label: "Edit Limit Exceeded", example: `{\n  "status": false,\n  "statusCode": 1022,\n  "message": " Your editBooking try is extend to maximum limit."\n}` },
      { code: 1023, type: "error", label: "Booking Not Found", example: `{\n  "status": false,\n  "statusCode": 1023,\n  "message": "Booking Not Found"\n}` },
      { code: 500, type: "error", label: "Server Error", example: `{\n  "status": false,\n  "statusCode": 500,\n  "message": "Internal Server Error"\n}` },
    ],
    notes: [
      "Cannot edit if booking is ONGOING (driver arrived or beyond).",
      "Cannot edit pickup location, passenger info, or pickup time once booking is ACCEPTED by driver.",
      "An edit limit is enforced by admin — you will get 1022 if exceeded.",
      "Driver, dispatcher, and passenger are notified via push notification after each edit.",
      "An SMS is also sent to the passenger after successful edit.",
      "Webhook callback is fired to your registered URL after edit.",
    ],
  },

  {
    id: "cancel-order",
    name: "Cancel Order",
    method: "POST",
    endpoint: "/corporate/cancel/booking",
    description:
      "Cancels an existing booking. Only allowed at specific booking statuses. Returns cancellation charges if applicable.",
    headers: [
      { key: "bearerkey", value: "xxxxx@786{companyId}", required: true, description: "Same bearerkey used in Corporate Authorize." },
      { key: "Content-Type", value: "application/json", required: true, description: "Request body format." },
    ],
    body: [
      { field: "booking_id", type: "string", required: true, description: "The booking ID to cancel.", example: "BK123456" },
      { field: "cancellationReason", type: "string", required: false, description: "Reason for cancellation.", example: "Passenger no longer needs the ride" },
    ],
    requestExample: `{
  "booking_id": "BK123456",
  "cancellationReason": "Passenger no longer needs the ride"
}`,
    responses: [
      {
        code: 200,
        type: "success",
        label: "Booking Cancelled",
        example: `{
  "status": true,
  "statusCode": 200,
  "data": {
    "cancellation_charges": 0.500
  }
}`,
      },
      { code: 422, type: "error", label: "Missing Parameters", example: `{\n  "status": false,\n  "statusCode": 422,\n  "message": "Required Parameters Missing"\n}` },
      { code: 1011, type: "error", label: "Cancel Failed", example: `{\n  "status": false,\n  "statusCode": 1011,\n  "message": "Cancel order Failed"\n}` },
      { code: 1013, type: "error", label: "Cannot Cancel", example: `{\n  "status": false,\n  "statusCode": 1013,\n  "message": "Cannot cancel booking. Driver has already completed the drop-off"\n}` },
      { code: 1014, type: "error", label: "Booking Not Found", example: `{\n  "status": false,\n  "statusCode": 1014,\n  "message": "Booking Not Found"\n}` },
      { code: 1015, type: "error", label: "User Not Active", example: `{\n  "status": false,\n  "statusCode": 1015,\n  "message": "User not active or does not exist. Please contact support."\n}` },
      { code: 500, type: "error", label: "Server Error", example: `{\n  "status": false,\n  "statusCode": 500,\n  "message": "Internal Server Error"\n}` },
    ],
    notes: [
      "Cancellation is allowed when status is: PENDING, ACCEPTED, DRIVER ARRIVED, or ON TRIP (before slide-to-complete).",
      "Cancellation is NOT allowed once the driver has completed the drop-off.",
      "cancellation_charges is 0.0 if no charge applies.",
      "Cancellation charges depend on admin configuration and timing.",
    ],
  },
];
